import { DeviceIcon } from 'app/agriculture/icons/devices/DeviceIcon';
import { useCallback, useMemo, useState } from 'react';
import { SearchField } from 'app/agriculture/infobox/common/SearchField/SearchField';
import './DeviceListComponent.scss';
import { Button } from 'app/agriculture/components/Button/Button';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { EmptyState } from 'app/agriculture/components/EmptyState/EmptyState';
import { Collapse } from '@mui/material';
import classNames from 'classnames';
import { DeviceButtonFilters } from 'app/agriculture/routes/Devices/DeviceList/components/DeviceButtonFilters/DeviceButtonFilters';
import { DeviceListTable } from 'app/agriculture/routes/Devices/DeviceList/components/DeviceListTable';
import { SuspenseWrapper } from 'app/agriculture/components/SuspenseWrapper/SuspenseWrapper';
import { DeviceListTableSkeleton } from 'app/agriculture/routes/Devices/DeviceList/components/DeviceListTableSkeleton';
import { useDeviceResults } from 'app/agriculture/routes/Devices/DeviceList/hooks/useDeviceResults';

export function DeviceListComponent() {
    const [textFilter, setTextFilter] = useState('');
    const [buttonFilterIds, setButtonFilterIds] = useState<string[]>([]);
    const [disableTableSort, setDisableTableSort] = useState(false);
    const deviceResults = useDeviceResults(textFilter);
    const filteredDeviceResults = useMemo(
        () =>
            deviceResults.filter(
                (device) =>
                    buttonFilterIds.length === 0 || buttonFilterIds.includes(device.deviceType),
            ),
        [buttonFilterIds, deviceResults],
    );

    const handleTextFilterChange = useCallback((textFilter: string) => {
        setTextFilter(textFilter);
        setDisableTableSort(true);
    }, []);

    const handleTableSort = useCallback(() => {
        setDisableTableSort(false);
    }, []);

    const [isSearchOpen, setIsSearchOpen] = useState(false);

    return (
        <ContentBox
            title="Devices"
            Icon={(props) => <DeviceIcon {...props} deviceType="Yarn Relay" />}
            className="DeviceList"
            variant="xl"
        >
            <ContentBox.Section className="DeviceList__header">
                <Button
                    id="open-search-button"
                    className={classNames('SearchButton', {
                        'SearchButton--selected': isSearchOpen,
                    })}
                    startIcon="search"
                    variant="textonly"
                    onClick={() => {
                        setIsSearchOpen(!isSearchOpen);
                    }}
                    text={!isSearchOpen ? 'Search' : 'Exit Search'}
                />
                <Collapse in={isSearchOpen}>
                    <div className="NoteListHeader__search-tray">
                        <SearchField textFilter={textFilter} onChange={handleTextFilterChange} />
                        <DeviceButtonFilters
                            filterIds={buttonFilterIds}
                            onFilterIdsChange={setButtonFilterIds}
                        />
                    </div>
                </Collapse>
            </ContentBox.Section>
            <ContentBox.Section scrollable>
                {filteredDeviceResults.length === 0 ? (
                    <EmptyState
                        message={
                            !textFilter
                                ? 'There are currently no devices'
                                : 'Your search returned no results'
                        }
                    />
                ) : (
                    <SuspenseWrapper loadingComponent={<DeviceListTableSkeleton />}>
                        <DeviceListTable
                            devices={filteredDeviceResults}
                            disableSort={disableTableSort}
                            onSort={handleTableSort}
                        />
                    </SuspenseWrapper>
                )}
            </ContentBox.Section>
        </ContentBox>
    );
}
